/**
 * referenceNaming — how an import decides a reference's model / collection /
 * series strings.
 *
 * Extracted from BulkImport's `mapEntryToReference` so the one rule that matters
 * here is testable on its own: **source casing is preserved verbatim**.
 *
 * The importer used to title-case model and collection with
 * `str.replace(/\b\w/g, c => c.toUpperCase())`. In a non-unicode JS regex `\w`
 * is `[A-Za-z0-9_]`, so every accented letter is a word BOUNDARY: "Panthère de
 * Cartier" came back as "PanthèRe De Cartier", and the same happened to every
 * French, German and Spanish name in the catalogue. Adding the `u` flag would
 * not have fixed it either — "de" is genuinely lowercase in the maker's own
 * name, and no casing heuristic knows that.
 *
 * The extractor emits the maker's own casing. Trust it.
 */

/**
 * A model / collection / series / product name as the source wrote it.
 *
 * Whitespace-trimmed and nothing else — no title-casing, no upper-casing, no
 * particle handling. Nullish becomes '' so the caller can store an explicit
 * empty rather than `undefined` (Firestore rejects `undefined`).
 */
export function normalizeSourceName(value: string | null | undefined): string {
  return (value ?? '').trim();
}

/**
 * Series from a model name — its first whitespace-delimited word, e.g.
 * "Alpine Chronograph" → "Alpine". Returns undefined when there is nothing to
 * take, so the caller can omit the field entirely.
 *
 * The word is returned exactly as it appears in the model, which is the whole
 * point: "Chronomètre" must not come back as "ChronomèTre".
 */
export function deriveSeries(model: string | null | undefined): string | undefined {
  const first = (model ?? '').trim().split(/\s+/)[0];
  return first || undefined;
}
